/**
 * HopeContextChat - Context-aware chat interface for Hope AI
 *
 * Uses Claude (via /api/generate-notes?mode=hope-chat) for empathetic,
 * non-clinical conversation. Patient context is sent with the first message.
 * Crisis detection stays client-side for instant response.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { detectCrisis, storeConversation } from '../services/ConvaiService';
import { crisisNotificationService } from '../services/CrisisNotificationService';
import { auth } from '../config/firebase';
import { HopeVoiceService } from '../services/HopeVoiceService';

// Web Speech API — use 'any' to avoid conflicts with lib.dom.d.ts declarations
type SpeechRecognitionAny = any;

interface Message {
  sender: 'user' | 'hope';
  text: string;
  timestamp: Date;
}

interface HopeContextChatProps {
  patientContext: string;
  characterId: string;
  apiKey: string;
  patientId?: string;
  patientName?: string;
  suggestedTopics?: string[];
}

export default function HopeContextChat({
  patientContext,
  characterId,
  apiKey,
  patientId,
  patientName,
  suggestedTopics = [],
}: HopeContextChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(true); // Always connected with Claude
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showContactProvider, setShowContactProvider] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognitionAny>(null);

  // Check browser support
  const hasSpeechRecognition = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in (window as any));
  const hasSpeechSynthesis = typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Text-to-speech for Hope's responses — uses ElevenLabs (Hope voice)
  const speakResponse = useCallback((text: string) => {
    if (!voiceEnabled) return;
    // Use ElevenLabs via HopeVoiceService for natural voice
    HopeVoiceService.speak(text);
  }, [voiceEnabled]);

  // Speech-to-text microphone input
  const toggleListening = useCallback(() => {
    if (!hasSpeechRecognition) return;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInputText((prev: string) => prev ? `${prev} ${transcript}` : transcript);
    };
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, hasSpeechRecognition]);

  // Cleanup recognition and voice on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      HopeVoiceService.stop();
      if (hasSpeechSynthesis) speechSynthesis.cancel();
    };
  }, [hasSpeechSynthesis]);

  // Helper function to get current time string with timezone
  const getCurrentTimeContext = (): string => {
    const now = new Date();

    // Determine timezone from patient context
    // Extract timezone from the static context (it should contain "Patient's timezone: America/New_York")
    const timezoneMatch = patientContext.match(/Patient's timezone: ([^\s.]+)/);
    const patientTimezone = timezoneMatch ? timezoneMatch[1] : 'America/New_York';

    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: patientTimezone
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: patientTimezone
    };

    const currentDate = now.toLocaleDateString('en-US', dateOptions);
    const currentTime = now.toLocaleTimeString('en-US', timeOptions);

    return `Current system time: ${currentTime} on ${currentDate}. Patient's timezone: ${patientTimezone}.`;
  };

  // Helper function to inject fresh time into patient context
  const getFreshContext = (): string => {
    // Remove old time context (first two sentences about time)
    const withoutTimeContext = patientContext.replace(
      /Current system time: [^.]+\. Patient's timezone: [^.]+\./,
      ''
    ).trim();

    // Add fresh time context at the beginning
    return `${getCurrentTimeContext()} ${withoutTimeContext}`;
  };

  /**
   * Send a message to Hope via Claude API (reuses generate-notes endpoint with mode: 'hope-chat')
   */
  const sendToHope = useCallback(async (
    message: string,
    context?: string,
    history: Message[] = []
  ): Promise<string> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();

    const response = await fetch('/api/generate-notes', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        mode: 'hope-chat',
        message,
        context,
        conversationHistory: history.map(m => ({ sender: m.sender, text: m.text })),
      }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Hope API error ${response.status}: ${errText}`);
    }

    const data = await response.json();
    return data.text;
  }, []);

  // Send initial greeting via Claude
  useEffect(() => {
    if (hasGreeted) return;

    // Check for cached greeting
    const cacheKey = `hope_greeting_${patientId || 'default'}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { text, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 10 * 60 * 1000) {
          setMessages([{ sender: 'hope', text, timestamp: new Date(timestamp) }]);
          setHasGreeted(true);
          speakResponse(text);
          return;
        }
      } catch { /* ignore parse errors */ }
    }

    // Fetch greeting from Claude
    setIsLoading(true);
    setHasGreeted(true);

    sendToHope('Hello', getFreshContext())
      .then((text) => {
        setMessages([{ sender: 'hope', text, timestamp: new Date() }]);
        speakResponse(text);
        // Cache the greeting
        sessionStorage.setItem(cacheKey, JSON.stringify({ text, timestamp: Date.now() }));
      })
      .catch((err) => {
        console.error('[Hope] Greeting failed:', err);
        setMessages([{
          sender: 'hope',
          text: "Hi there! I'm Hope, your wellness companion. How are you feeling today?",
          timestamp: new Date(),
        }]);
      })
      .finally(() => setIsLoading(false));
  }, [hasGreeted, patientId, sendToHope, speakResponse]);

  // Cache greeting after first Hope response
  useEffect(() => {
    if (hasGreeted && messages.length === 1 && messages[0].sender === 'hope') {
      const cacheKey = `hope_greeting_${patientId || 'default'}`;
      sessionStorage.setItem(cacheKey, JSON.stringify({
        text: messages[0].text,
        timestamp: Date.now(),
      }));
    }
  }, [messages, hasGreeted, patientId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText.trim();
    setInputText('');

    // Add user message to chat
    const userMsg: Message = { sender: 'user', text: userMessage, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      // Detect crisis before sending to Claude (client-side, instant)
      const crisisDetection = detectCrisis(userMessage);

      // If crisis detected (high or critical severity), notify therapist with full transcript
      if (crisisDetection.detected && (crisisDetection.severity === 'high' || crisisDetection.severity === 'critical') && patientId && patientName) {
        try {
          const providerInfo = await crisisNotificationService.getPatientProvider(patientId);
          if (providerInfo) {
            const transcript = [...messages, userMsg].map(m => ({
              sender: m.sender,
              text: m.text,
              time: m.timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
            }));
            await crisisNotificationService.createHopeAlert({
              patientId,
              patientName,
              providerId: providerInfo.providerId,
              providerName: providerInfo.providerName,
              alertType: 'crisis',
              severity: crisisDetection.severity,
              keywords: crisisDetection.keywords,
              triggerMessage: userMessage,
              conversationTranscript: transcript,
            });
          }
        } catch (alertError) {
          console.error('Failed to create crisis alert:', alertError);
        }
      }

      // Send to Claude via generate-notes endpoint (hope-chat mode)
      // Pass conversation history so Claude has context of the full conversation
      const currentMessages = [...messages, userMsg];
      const isFirstMessage = messages.filter(m => m.sender === 'user').length === 0;
      const hopeResponse = await sendToHope(
        userMessage,
        isFirstMessage ? getFreshContext() : undefined,
        currentMessages
      );

      setMessages((prev) => [...prev, {
        sender: 'hope' as const,
        text: hopeResponse,
        timestamp: new Date(),
      }]);
      speakResponse(hopeResponse);

      // Store conversation exchange for provider review
      if (patientId) {
        const sessionId = `hope_${Date.now()}`;
        storeConversation(
          patientId,
          sessionId,
          userMessage,
          hopeResponse,
          crisisDetection.detected,
          crisisDetection.keywords
        ).catch(err => console.warn('[Hope] Failed to store conversation:', err));
      }

      // Urgency detection: suggest contacting provider for non-crisis but concerning patterns
      const urgencyPatterns = /can't sleep|getting worse|not working|don't want to go|skip.*appointment|stopped.*medication|quit.*therapy|giving up|no point|what's the use|feeling hopeless/i;
      if (urgencyPatterns.test(userMessage) && !crisisDetection.detected) {
        setMessages(prev => [...prev, {
          sender: 'hope' as const,
          text: "It sounds like this is really weighing on you. Would you like me to help you reach out to your provider? They'd want to know how you're feeling.",
          timestamp: new Date(),
        }]);
        setShowContactProvider(true);

        // Send urgency alert to provider with transcript + contact info
        if (patientId && patientName) {
          crisisNotificationService.getPatientProvider(patientId).then(providerInfo => {
            if (!providerInfo) return;
            const transcript = [...messages, userMsg].map(m => ({
              sender: m.sender,
              text: m.text,
              time: m.timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
            }));
            crisisNotificationService.createHopeAlert({
              patientId,
              patientName,
              providerId: providerInfo.providerId,
              providerName: providerInfo.providerName,
              alertType: 'urgency',
              severity: 'urgency',
              keywords: (userMessage.match(urgencyPatterns) || []).map(m => m.toLowerCase()),
              triggerMessage: userMessage,
              conversationTranscript: transcript,
            }).catch(err => console.warn('[Hope] Failed to send urgency alert:', err));
          }).catch(() => {});
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [...prev, {
        sender: 'hope' as const,
        text: "I'm sorry, I had trouble processing that. Please try again.",
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={styles.chatContainer}>
      {/* Status bar (compact) */}
      <div style={styles.statusBar} aria-live="polite">
        {isListening ? '🎤 Listening...' : isLoading ? '💬 Typing...' : '🟢 Online'}
      </div>

      {/* Screen reader announcement for new messages */}
      <div aria-live="polite" aria-atomic="false" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
        {messages.length > 0 && messages[messages.length - 1].sender === 'hope' && (
          <span>Hope says: {messages[messages.length - 1].text}</span>
        )}
      </div>

      {/* Messages Area */}
      <div style={styles.messagesArea} role="log" aria-label="Chat messages">
        {messages.map((msg, index) => (
          <div
            key={index}
            style={
              msg.sender === 'user'
                ? styles.userMessageContainer
                : styles.hopeMessageContainer
            }
          >
            <div
              style={msg.sender === 'user' ? styles.userMessage : styles.hopeMessage}
            >
              {msg.text}
            </div>
            <div style={styles.timestamp}>
              {msg.timestamp.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </div>
          </div>
        ))}
        {isLoading && (
          <div style={styles.hopeMessageContainer}>
            <div style={styles.typingIndicator}>
              <span style={styles.typingDot}></span>
              <span style={styles.typingDot}></span>
              <span style={styles.typingDot}></span>
            </div>
          </div>
        )}
        {/* Contact Provider button — shown when urgency detected */}
        {showContactProvider && (
          <div style={{ padding: '8px 12px', margin: '8px 12px', backgroundColor: 'rgba(59,130,246,0.15)', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.3)', textAlign: 'center' }}>
            <button
              onClick={() => {
                // Navigate to messages — parent component should handle this
                window.dispatchEvent(new CustomEvent('hope-contact-provider', { detail: { patientId, patientName } }));
                setShowContactProvider(false);
              }}
              style={{
                padding: '8px 20px', backgroundColor: '#3B82F6', color: 'white',
                border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Message My Provider
            </button>
            <button
              onClick={() => setShowContactProvider(false)}
              style={{ padding: '4px 12px', background: 'none', border: 'none', color: '#9ca3af', fontSize: '12px', cursor: 'pointer', marginLeft: '8px' }}
            >
              Not right now
            </button>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Topics */}
      {showSuggestions && suggestedTopics.length > 0 && messages.length <= 1 && (
        <div style={styles.suggestionsArea} role="group" aria-label="Suggested topics">
          {suggestedTopics.map((topic, i) => (
            <button
              key={i}
              onClick={() => {
                setInputText(topic);
                setShowSuggestions(false);
              }}
              style={styles.suggestionChip}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#e0e7ff';
                e.currentTarget.style.borderColor = '#6366f1';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f4ff';
                e.currentTarget.style.borderColor = '#c7d2fe';
              }}
            >
              {topic}
            </button>
          ))}
        </div>
      )}

      {/* Input Area */}
      <div style={styles.inputArea}>
        {/* Voice toggle */}
        {hasSpeechSynthesis && (
          <button
            onClick={() => { setVoiceEnabled(!voiceEnabled); if (!voiceEnabled) speechSynthesis.cancel(); }}
            style={{
              ...styles.voiceToggle,
              backgroundColor: voiceEnabled ? '#e8f5e9' : '#f5f5f5',
              color: voiceEnabled ? '#2e7d32' : '#999',
            }}
            aria-label={voiceEnabled ? 'Disable voice responses' : 'Enable voice responses'}
            title={voiceEnabled ? 'Voice on' : 'Voice off'}
          >
            {voiceEnabled ? '🔊' : '🔇'}
          </button>
        )}

        {/* Mic button for speech-to-text */}
        {hasSpeechRecognition && (
          <button
            onClick={toggleListening}
            style={{
              ...styles.micButton,
              backgroundColor: isListening ? '#ffebee' : '#f5f5f5',
              borderColor: isListening ? '#ef5350' : '#ddd',
            }}
            aria-label={isListening ? 'Stop listening' : 'Start voice input'}
            disabled={isLoading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill={isListening ? '#ef5350' : '#666'}>
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            </svg>
            {isListening && <span style={styles.listeningPulse} />}
          </button>
        )}

        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isListening ? 'Listening...' : 'Type your message...'}
          style={styles.input}
          disabled={isLoading}
          aria-label="Message input"
          autoFocus
        />
        <button
          onClick={handleSend}
          style={{
            ...styles.sendButton,
            opacity: isLoading || !inputText.trim() ? 0.5 : 1,
            cursor: isLoading || !inputText.trim() ? 'not-allowed' : 'pointer',
          }}
          disabled={isLoading || !inputText.trim()}
          aria-label="Send message"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="white">
            <path d="M2 10L18 2L10 18L8 11L2 10Z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  statusBar: {
    padding: '6px 16px',
    fontSize: '12px',
    color: '#28a745',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e8e8e8',
    textAlign: 'center',
    flexShrink: 0,
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: '#f5f5f5',
  },
  userMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  hopeMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  userMessage: {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '10px 16px',
    borderRadius: '18px 18px 4px 18px',
    maxWidth: '70%',
    wordWrap: 'break-word',
    fontSize: '14px',
    lineHeight: '1.4',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  },
  hopeMessage: {
    backgroundColor: '#fff',
    color: '#333',
    padding: '10px 16px',
    borderRadius: '18px 18px 18px 4px',
    maxWidth: '70%',
    wordWrap: 'break-word',
    fontSize: '14px',
    lineHeight: '1.4',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
  },
  timestamp: {
    fontSize: '11px',
    color: '#999',
    marginTop: '4px',
    paddingLeft: '4px',
    paddingRight: '4px',
  },
  typingIndicator: {
    backgroundColor: '#fff',
    padding: '10px 16px',
    borderRadius: '18px',
    display: 'flex',
    gap: '4px',
    border: '1px solid #e0e0e0',
  },
  typingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#999',
    animation: 'typing 1.4s infinite',
  },
  inputArea: {
    display: 'flex',
    padding: '16px 20px',
    borderTop: '1px solid #e0e0e0',
    gap: '12px',
    backgroundColor: '#fff',
  },
  input: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '24px',
    border: '1px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  sendButton: {
    padding: '12px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
    minWidth: '48px',
  },
  micButton: {
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
    transition: 'all 0.2s',
    minWidth: '44px',
    minHeight: '44px',
  },
  voiceToggle: {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    transition: 'all 0.2s',
    minWidth: '40px',
    minHeight: '40px',
  },
  suggestionsArea: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px 20px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#fafbfc',
  },
  suggestionChip: {
    padding: '6px 14px',
    borderRadius: '16px',
    border: '1px solid #c7d2fe',
    backgroundColor: '#f0f4ff',
    color: '#4338ca',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  listeningPulse: {
    position: 'absolute' as const,
    top: '-2px',
    right: '-2px',
    width: '10px',
    height: '10px',
    backgroundColor: '#ef5350',
    borderRadius: '50%',
    animation: 'pulse 1s infinite',
  },
};
